import React, { Fragment, useRef, useState, } from "react";
import { useLayoutEffect } from "react";
import { useEffect } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { GridColumnAlignment } from "../types/table";
import { classNames } from "../utils/classNames";
import { SelectAllTableCheckbox } from "./SelectAllTableCheckbox";
import { SelectTableRowCheckbox } from "./SelectTableRowCheckbox";
import { SelectTableRowRadio } from "./SelectTableRowRadio";
import { SkeletonCheckbox } from "./SkeletonCheckbox";
import { TableEmpty, TableWrapper, TD, TDAction } from "./TableComponents";
import { TableDropdown } from "./TableDropdown";
import { TableBodyForm, TableForm, TableHeadForm, } from "./TableForm";
import { getCellId, getDataTestId, getHiddenClassesFromBreakpoints, getItemNodeFormatting, getItemStringFormatting, getTableColumnWidth, } from "./tableUtils";
export function Table({ headings, data = [], onSelect, link, disabledRows, loading, error, dropdown, noDataText = "No records found", id, form, actions, dataId, dataTestId, dataLabel, view, }) {
    if (!loading && !error && !data) {
        console.warn("[piston-ux] Loading will be REQUIRED in a future release.");
    }
    if (form) {
        return (React.createElement(TableForm, { form: form, id: id, headings: headings, dataId: dataId, dataTestId: dataTestId, dataLabel: dataLabel, view: view }, form.type === "full" ? (React.createElement(Fragment, null,
            React.createElement(TableHeadForm, { headings: headings, data: data, endRowColumn: !!(dropdown &&
                    dropdown.length &&
                    (!form?.viewState || form?.viewState === "read")), form: form, loading: loading, onSelect: onSelect, view: view }),
            React.createElement(TableBodyForm, { headings: headings, onSelect: onSelect, link: link, disabledRows: disabledRows, loading: loading, error: error, dropdown: dropdown, noDataText: noDataText, id: id, form: form, actions: actions }))) : (React.createElement(Fragment, null,
            React.createElement(TableHead, { headings: headings, data: data, dropdown: dropdown, form: form, loading: loading, tableHasData: (data?.length ?? 0) > 0, endRowColumn: !!(getEndRowColumn(onSelect, dropdown, link) &&
                    (!form?.viewState || form?.viewState === "read")), view: view }),
            React.createElement(TableBody, { headings: headings, data: data, onSelect: onSelect, link: link, disabledRows: disabledRows, loading: loading, error: error, dropdown: dropdown, noDataText: noDataText, id: id, form: form, actions: actions })))));
    }
    return (React.createElement(TableWrapper, { id: id, dataId: dataId, dataTestId: dataTestId, dataLabel: dataLabel, view: view },
        React.createElement(TableHead, { headings: headings, data: data, dropdown: dropdown, form: form, loading: loading, endRowColumn: getEndRowColumn(onSelect, dropdown, link), view: view }),
        React.createElement(TableBody, { headings: headings, data: data, onSelect: onSelect, link: link, disabledRows: disabledRows, loading: loading, error: error, dropdown: dropdown, noDataText: noDataText, id: id, form: form, actions: actions })));
}
export function TableHead({ headings, data, form, loading, endRowColumn, view, }) {
    const tableHasData = (data?.length ?? 0) > 0;
    return (React.createElement("thead", null,
        React.createElement("tr", null,
            form && form.type !== "full" && (React.createElement("th", { key: "select", className: "table--select" }, form.type === "checkbox" &&
                (!tableHasData || loading ? (React.createElement(SkeletonCheckbox, null)) : (React.createElement(SelectAllTableCheckbox, { data: data, form: form }))))),
            headings.map((heading, i) => (React.createElement(TH, { heading: heading, index: i, view: view, headingsLength: headings.length, key: i }))),
            endRowColumn && (React.createElement("th", { key: "action", style: { width: 32, maxWidth: 32 } })))));
}
export function TH({ heading, index, view, headingsLength, }) {
    const [height, setHeight] = useState(undefined);
    const ref = useRef(null);
    const updateHeight = () => setHeight(ref?.current?.offsetHeight);
    useLayoutEffect(() => {
        window.addEventListener("resize", updateHeight);
        updateHeight();
        return () => window.removeEventListener("resize", updateHeight);
    }, [ref, view]);
    useEffect(() => {
        if (view === "contained" && ref)
            updateHeight();
    }, [ref, view]);
    return (React.createElement("th", { onClick: () => (heading.onClick ? heading.onClick(heading) : undefined), id: heading.id, className: classNames(getHiddenClassesFromBreakpoints(heading.hiddenBreakpoints), heading.onClick ? "table__sortable" : "", getTableColumnWidth(heading.width), `text-${heading.alignment ?? GridColumnAlignment.LEFT}`), ref: ref },
        view === "contained" && index === 0 && (React.createElement("span", { className: "th--firstChild", style: { height } })),
        React.createElement("span", { className: heading.width && "table__column--inner" }, heading.title),
        view === "contained" && index + 1 === headingsLength && (React.createElement("span", { className: "th--lastChild", style: { height } }))));
}
function TableBody({ headings, data, onSelect, link, disabledRows, loading, error, dropdown, noDataText, id, form, actions, }) {
    const t = useTranslation();
    const tableHasData = (data?.length ?? 0) > 0;
    const rowStyle = {
        cursor: onSelect ? "pointer" : undefined,
    };
    let columnNumbers = headings.length;
    columnNumbers = getNumberOfColumnsToAlignWithHeader(columnNumbers, form);
    if (loading) {
        return (React.createElement(TableEmpty, { text: `${t("Loading")}...`, columnNumbers: columnNumbers }));
    }
    if (error || data === undefined) {
        return (React.createElement(TableEmpty, { text: "Error getting data", columnNumbers: columnNumbers }));
    }
    if (!tableHasData) {
        return React.createElement(TableEmpty, { text: noDataText, columnNumbers: columnNumbers });
    }
    return (React.createElement("tbody", null, data.map((item, i) => {
        const rowDisabled = getDisabledRow(item, disabledRows);
        return (React.createElement("tr", { key: i, style: rowDisabled ? undefined : rowStyle },
            React.createElement(TableData, { headings: headings, item: item, rowDisabled: rowDisabled, rowId: i, data: data, onSelect: onSelect, link: link, dropdown: dropdown, id: id, form: form, actions: actions })));
    })));
}
function getDisabledRow(item, disabledRows) {
    const getRowDisabled = (disabled) => {
        if (disabled === undefined)
            return false;
        if (typeof disabled === "boolean")
            return disabled;
        return disabled(item);
    };
    return getRowDisabled(disabledRows);
}
function TableData({ headings, data, onSelect, link, dropdown, id, form, item, rowDisabled, rowId, actions, dataTestId, }) {
    const tableHasData = (data?.length ?? 0) > 0;
    const handleClick = React.useCallback((ev, data) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (onSelect) {
            onSelect(data);
        }
    }, [onSelect]);
    const cellId = getCellId(id, rowId);
    let itemUrl = link?.url + (link?.linkKey ? item[link.linkKey] : "");
    const LinkDropdown = link?.routerLink;
    const hasDropdown = dropdown && dropdown.length && tableHasData;
    return (React.createElement(Fragment, null,
        form?.values && form.labelIdentifier && form.idIdentifier && (React.createElement("td", { className: "table--select" },
            form.type === "checkbox" && (React.createElement(SelectTableRowCheckbox, { label: item[form.labelIdentifier], name: form.name, id: item[form.idIdentifier] })),
            form.type === "radiogroup" && (React.createElement(SelectTableRowRadio, { label: item[form.labelIdentifier], name: form.name, id: item[form.idIdentifier] })))),
        headings.map((heading, i) => {
            const itemNode = getItemNodeFormatting(heading, item);
            const itemString = heading.tipFormatter &&
                getItemStringFormatting(heading, item);
            const dataTestId = getDataTestId(heading, rowId);
            if (link && !rowDisabled) {
                const emptyDiv = (React.createElement("div", { className: "empty", "aria-label": "empty cell" }));
                return (React.createElement(TD, { hiddenBreakpoints: heading.hiddenBreakpoints, link: true, key: i, width: heading.width, alignment: heading.alignment, tip: itemString, dataTestId: dataTestId },
                    React.createElement(TDLink, { LinkDropdown: LinkDropdown, itemUrl: itemUrl, cellId: cellId, itemNode: itemNode, emptyDiv: emptyDiv })));
            }
            return (React.createElement(TD, { onClick: (e) => (!rowDisabled ? handleClick(e, item) : null), hiddenBreakpoints: heading.hiddenBreakpoints, key: i, cellId: cellId, width: heading.width, alignment: heading.alignment, tip: itemString, dataTestId: dataTestId }, itemNode));
        }),
        getEndRowColumn(onSelect, dropdown, link) && (React.createElement(TDAction, { dataId: actions?.dataId, dataLabel: actions?.dataLabel, dataTestId: actions?.dataTestId, rowId: rowId },
            hasDropdown && (React.createElement(TableDropdown, { key: "actions", item: item, dropdown: dropdown, tableId: id, rowId: rowId })),
            onSelect && !hasDropdown && !link && !rowDisabled && (React.createElement(TDChevron, { onClick: (e) => (!rowDisabled ? handleClick(e, item) : null), dataTestId: `${actions?.dataTestId}-chevron` })),
            link && !hasDropdown && !onSelect && !rowDisabled && (React.createElement(TDLink, { LinkDropdown: LinkDropdown, itemUrl: itemUrl, cellId: cellId, itemNode: React.createElement(TDChevron, null) })),
            rowDisabled && !hasDropdown && (onSelect || link) && rowDisabled && (React.createElement(TDChevron, { disabled: true }))))));
}
const TDChevron = ({ disabled, onClick, dataTestId }) => (React.createElement("span", { className: classNames("glyphicon glyphicon-chevron-right table__row-action", disabled ? "table__disabled" : ""), onClick: onClick, "data-testid": dataTestId }));
const TDLink = ({ LinkDropdown, itemUrl, cellId, itemNode, emptyDiv }) => (React.createElement(Fragment, null, LinkDropdown ? (React.createElement(LinkDropdown, { to: itemUrl, href: itemUrl, id: cellId }, itemNode || emptyDiv)) : (React.createElement("a", { href: itemUrl, id: cellId }, itemNode || emptyDiv))));
function getNumberOfColumnsToAlignWithHeader(columnNumbers, form) {
    columnNumbers =
        form?.values && form.type !== "full" ? columnNumbers + 1 : columnNumbers;
    return columnNumbers + 1;
}
function getEndRowColumn(onSelect, dropdown, link) {
    return !!(onSelect || (dropdown && dropdown.length) || link);
}
